/**
 * ActivePropertyContext — global "which property are we working on?" store.
 *
 * The whole point: pick a property ONCE (from Pipeline, Leads, or the top-bar
 * pill) and every Intelligence Engine, Command Center tab, Jarvis command,
 * and quick action reads from the SAME record. No more "Add a property"
 * empty states scattered across each engine page.
 *
 * Priority for what gets selected:
 *   1. `?property_id=xxx` in the current URL — deep-links win
 *   2. `localStorage.v3_intel_property_id` — last property the operator picked
 *   3. First property in the workspace list — safe fallback so pages aren't blank
 *
 * Every setter call:
 *   • updates React state
 *   • persists to localStorage
 *   • rewrites `?property_id=` in the URL when we're on an intel/command-center
 *     route (so shareable and back-button friendly)
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

const STORAGE_KEY = "v3_intel_property_id";
const CTX = createContext(null);

// Any route where the ?property_id= query is meaningful. On these routes we
// mirror the selected property into the URL so it's shareable + back-nav safe.
function routeCaresAboutPropertyId(pathname) {
  if (!pathname) return false;
  return (
    pathname.startsWith("/v3/intel/") ||
    pathname === "/v3/tools/command-center" ||
    pathname.startsWith("/v3/tools/command-center/")
  );
}

// Routes that carry the property id as the LAST path segment (Property
// Command Center-style pages). When the user picks a new property from
// the switcher on these pages, we rewrite the last segment so the page
// re-mounts against the newly selected id instead of silently drifting
// out of sync with the pill.
//   /v3/deal/:id, /v3/deals/:id, /v3/properties/:id, /v3/property/:id
const DEAL_PATH_RE = /^(\/v3\/(?:deal|deals|properties|property))\/[^/?#]+/;
function rewriteDealPath(pathname, newId) {
  const m = DEAL_PATH_RE.exec(pathname || "");
  if (!m) return null;
  return `${m[1]}/${newId}`;
}

function readInitialFromLocation(search) {
  try {
    const params = new URLSearchParams(search || "");
    const fromUrl = params.get("property_id");
    if (fromUrl) return fromUrl;
    return localStorage.getItem(STORAGE_KEY) || "";
  } catch { return ""; }
}

export function ActivePropertyProvider({ children }) {
  const location = useLocation();
  const nav = useNavigate();

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [propertyId, setPropertyIdState] = useState(
    () => readInitialFromLocation(location.search),
  );

  // Load workspace properties once on mount. Refresh() can be called by
  // any consumer that just wrote a new property (create, duplicate, etc).
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v3/properties?limit=200");
      setProperties(data.items || []);
    } catch {
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // If the URL supplies a ?property_id= (e.g. clicking an Intel link from a
  // Pipeline card), adopt it as the active property.
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const fromUrl = params.get("property_id");
      if (fromUrl && fromUrl !== propertyId) {
        setPropertyIdState(fromUrl);
        localStorage.setItem(STORAGE_KEY, fromUrl);
      }
    } catch { /* noop */ }
  }, [location.search, propertyId]);

  // Once the workspace properties load, if we still don't have a valid ID,
  // fall back to the first property so engine pages aren't empty.
  useEffect(() => {
    if (!properties.length) return;
    const known = new Set(properties.map((p) => p.id));
    if (!propertyId || !known.has(propertyId)) {
      const fallback = properties[0]?.id || "";
      if (fallback && fallback !== propertyId) {
        setPropertyIdState(fallback);
        // Deliberate: do NOT persist to localStorage — it's a convenience
        // default, not a user choice.
      }
    }
  }, [properties, propertyId]);

  const setPropertyId = useCallback((id) => {
    if (!id) {
      setPropertyIdState("");
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
      return;
    }
    if (id === propertyId) return;
    setPropertyIdState(id);
    try { localStorage.setItem(STORAGE_KEY, id); } catch { /* noop */ }
    // If the current route cares about ?property_id=, mirror it there so
    // the URL is shareable and back-navigation works.
    if (routeCaresAboutPropertyId(location.pathname)) {
      const params = new URLSearchParams(location.search);
      params.set("property_id", id);
      nav(
        { pathname: location.pathname, search: `?${params.toString()}` },
        { replace: true },
      );
      return;
    }
    // Property Command Center-style pages carry the id in the last path
    // segment. Rewrite it so the page re-mounts against the newly picked
    // property. If the URL is ALREADY on that id (e.g. this call is coming
    // from PropertyWorkspace's own mount-sync effect), skip the nav — it
    // would only bounce us back via a stale closure and create a loop.
    const m = DEAL_PATH_RE.exec(location.pathname || "");
    if (m) {
      const currentUrlId = location.pathname.slice(m[1].length + 1).split(/[/?#]/)[0];
      if (currentUrlId !== id) {
        nav({ pathname: `${m[1]}/${id}`, search: location.search });
      }
    }
  }, [propertyId, location.pathname, location.search, nav]);

  const selected = useMemo(
    () => properties.find((p) => p.id === propertyId) || null,
    [properties, propertyId],
  );

  const value = useMemo(
    () => ({ properties, selected, propertyId, setPropertyId, loading, refresh }),
    [properties, selected, propertyId, setPropertyId, loading, refresh],
  );

  return <CTX.Provider value={value}>{children}</CTX.Provider>;
}

/**
 * useActiveProperty — the primary API. Returns:
 *   { properties, selected, propertyId, setPropertyId, loading, refresh }
 *
 * Any component under <V3Shell/> can read + write the currently active
 * property. Outside the provider it returns a safe no-op object.
 */
export function useActiveProperty() {
  const ctx = useContext(CTX);
  if (ctx) return ctx;
  // Safe fallback when used outside the provider (e.g. marketing pages).
  return {
    properties: [],
    selected: null,
    propertyId: "",
    setPropertyId: () => { /* noop */ },
    loading: false,
    refresh: () => Promise.resolve(),
  };
}

export default ActivePropertyProvider;
